import { Link, useNavigate } from "react-router-dom";
import { Trash2, ShoppingCart, ArrowLeft, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { CountdownTimer } from "@/components/CountdownTimer";
import { EmptyState } from "@/components/EmptyState";
import { EventImage } from "@/components/EventImage";
import { AuthModal } from "@/components/AuthModal";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { getEventPublicPath } from "@/lib/urls";
import { useState, useEffect } from "react";
import { validateCoupon } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { formatBRL } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export default function Carrinho() {
  const { t } = useTranslation("checkout");
  const { items, removeItem, updateQuantity, clearCart, renewExpiry, subtotal, platformFee, expiresAt, couponCode, setCouponCode, discount, setDiscount, setAppliedCouponId, finalTotal } = useCart();
  const { user } = useAuth();
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [unavailableItems, setUnavailableItems] = useState<string[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (items.length === 0) return;
    const tierIds = items.map(i => i.tierId).filter(id => !id.startsWith("product-"));
    if (tierIds.length === 0) return;

    supabase
      .from("ticket_tiers")
      .select("id, quantity_total, quantity_sold, quantity_reserved, sale_start_date, sale_end_date")
      .in("id", tierIds)
      .then(({ data }) => {
        if (!data) return;
        const unavailable: string[] = [];
        const now = new Date();
        for (const item of items) {
          if (item.tierId.startsWith("product-")) continue;
          const tier = data.find(t => t.id === item.tierId);
          if (!tier) { unavailable.push(item.tierId); continue; }
          const available = tier.quantity_total - (tier.quantity_sold ?? 0) - (tier.quantity_reserved ?? 0);
          if (available < item.quantity) unavailable.push(item.tierId);
          if (tier.sale_start_date && new Date(tier.sale_start_date) > now) unavailable.push(item.tierId);
          if (tier.sale_end_date && new Date(tier.sale_end_date) < now) unavailable.push(item.tierId);
        }
        setUnavailableItems(unavailable);
        if (unavailable.length > 0) {
          toast({ title: t("cart.availabilityChangedTitle"), description: t("cart.availabilityChangedDesc"), variant: "destructive" });
        }
      });
  }, [items.length, t]);

  // Re-check availability every 30 seconds
  useEffect(() => {
    if (items.length === 0) return;
    const interval = setInterval(() => {
      const tierIds = items.map(i => i.tierId).filter(id => !id.startsWith("product-"));
      if (tierIds.length === 0) return;
      supabase
        .from("ticket_tiers")
        .select("id, quantity_total, quantity_sold, quantity_reserved, sale_start_date, sale_end_date")
        .in("id", tierIds)
        .then(({ data }) => {
          if (!data) return;
          const unavailable: string[] = [];
          const now = new Date();
          for (const item of items) {
            if (item.tierId.startsWith("product-")) continue;
            const tier = data.find(t => t.id === item.tierId);
            if (!tier) { unavailable.push(item.tierId); continue; }
            const available = tier.quantity_total - (tier.quantity_sold ?? 0) - (tier.quantity_reserved ?? 0);
            if (available < item.quantity) unavailable.push(item.tierId);
            if (tier.sale_start_date && new Date(tier.sale_start_date) > now) unavailable.push(item.tierId);
            if (tier.sale_end_date && new Date(tier.sale_end_date) < now) unavailable.push(item.tierId);
          }
          setUnavailableItems(unavailable);
        });
    }, 30000);
    return () => clearInterval(interval);
  }, [items]);

  // A11: Block checkout when items are unavailable
  const handleCheckout = () => {
    if (!user) { setShowAuth(true); return; }
    if (unavailableItems.length > 0) {
      toast({ title: t("cart.itemsUnavailableTitle"), description: t("cart.itemsUnavailableDesc"), variant: "destructive" });
      return;
    }
    navigate("/checkout");
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim() || items.length === 0) return;
    setValidatingCoupon(true);
    try {
      const eventId = items[0].eventId;
      const coupon = await validateCoupon(eventId, couponCode);
      if (coupon) {
        if (coupon.min_order_value && subtotal < coupon.min_order_value) {
          toast({ title: t("cart.couponMinOrderTitle"), description: t("cart.couponMinOrderDesc", { min: formatBRL(Number(coupon.min_order_value)) }), variant: "destructive" });
          setValidatingCoupon(false);
          return;
        }
        if (coupon.applicable_tier_ids && coupon.applicable_tier_ids.length > 0) {
          const cartTierIds = items.map(i => i.tierId);
          const hasValidTier = cartTierIds.some(tid => coupon.applicable_tier_ids!.includes(tid));
          if (!hasValidTier) {
            toast({ title: t("cart.couponNotApplicableTitle"), description: t("cart.couponNotApplicableDesc"), variant: "destructive" });
            setValidatingCoupon(false);
            return;
          }
        }

        // M05: Calculate discount only on applicable tiers
        let applicableSubtotal = subtotal;
        if (coupon.applicable_tier_ids && coupon.applicable_tier_ids.length > 0) {
          applicableSubtotal = items
            .filter(i => coupon.applicable_tier_ids!.includes(i.tierId))
            .reduce((sum, i) => sum + i.price * i.quantity, 0);
        }

        const discountAmount =
          coupon.discount_type === "percentage"
            ? applicableSubtotal * (coupon.discount_value / 100)
            : coupon.discount_value;
        setDiscount(Math.min(discountAmount, applicableSubtotal));
        setAppliedCouponId(coupon.id);
        toast({ title: t("cart.couponAppliedTitle"), description: t("cart.couponAppliedDesc", { amount: formatBRL(Math.min(discountAmount, applicableSubtotal)) }) });
      }
    } catch (err) {
      toast({ title: t("cart.couponInvalidTitle"), description: err?.message || t("cart.couponInvalidDesc"), variant: "destructive" });
      setDiscount(0);
      setAppliedCouponId(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="container max-w-6xl pt-4 lg:pt-10 pb-12">
        <EmptyState
          icon={<ShoppingCart className="h-12 w-12" />}
          title={t("cart.emptyTitle")}
          description={t("cart.emptyDescription")}
          actionLabel={t("cart.viewEvents")}
          onAction={() => navigate("/eventos")}
        />
      </div>
    );
  }

  return (
    <div className="container max-w-6xl pt-4 lg:pt-10 pb-8 lg:pb-12">
      <Link to="/eventos" className="inline-flex min-h-11 items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-3 rounded-lg focus-visible:ring-2 focus-visible:ring-ring">
        <ArrowLeft className="h-4 w-4" /> {t("cart.continueShopping")}
      </Link>

      <div className="mb-6 space-y-5">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-foreground">{t("cart.title")}</h1>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">{t("cart.subtitle")}</p>
        </div>
        {expiresAt && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-muted/60 px-3 py-2">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-xs text-muted-foreground">{t("cart.reservationExpiresIn")}</span>
              <div className="flex items-center gap-1.5 text-sm font-medium">
                <CountdownTimer expiresAt={expiresAt} onExpire={clearCart} hideIcon />
              </div>
            </div>
            <Button variant="ghost" size="sm" className="min-h-11 rounded-xl px-3 text-sm" onClick={renewExpiry}>
              {t("cart.renew")}
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-col lg:flex-row items-start gap-5 lg:gap-8">
        <div className="w-full flex-1 min-w-0 overflow-hidden rounded-2xl border border-border/70 bg-card divide-y divide-border/60">
          {items.map((item) => (
            <div key={item.tierId} className={`flex flex-col xl:flex-row xl:items-center gap-4 p-4 sm:p-5 ${unavailableItems.includes(item.tierId) ? "bg-destructive/5" : ""}`}>
              <div className="flex items-start gap-3 flex-1 min-w-0">
                {unavailableItems.includes(item.tierId) ? (
                  <AlertTriangle className="h-6 w-6 text-destructive shrink-0" />
                ) : (
                  <EventImage
                    src={item.coverImageUrl}
                    alt={item.eventTitle}
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl object-cover shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <Link to={getEventPublicPath(item.eventSlug, item.producerUsername)} className="font-display font-semibold text-foreground hover:underline underline-offset-4 block text-sm sm:text-base leading-snug break-words">{item.eventTitle}</Link>
                  <p className="mt-1 text-sm leading-snug text-muted-foreground break-words">{item.tierName}</p>
                  <p className="text-xs text-muted-foreground mt-1.5">
                    {item.price === 0 ? t("cart.free") : `${formatBRL(item.price)} ${t("cart.each")}`}
                  </p>
                  {unavailableItems.includes(item.tierId) && (
                    <p className="text-xs text-destructive mt-1 font-medium">{t("cart.insufficientAvailability")}</p>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between xl:justify-end gap-x-3 gap-y-2 xl:shrink-0">
                <div className="flex items-center gap-3">
                  <span className="sr-only min-[360px]:not-sr-only text-xs text-muted-foreground">{t("cart.quantityShort")}</span>
                  <select
                    value={item.quantity}
                    onChange={(e) => updateQuantity(item.tierId, Number(e.target.value))}
                    aria-label={`${t("cart.quantityShort")} ${item.eventTitle} — ${item.tierName}`}
                    className="h-11 min-w-16 bg-muted/70 text-foreground text-sm font-semibold rounded-xl px-3 py-2 border border-transparent focus-visible:ring-2 focus-visible:ring-ring outline-none"
                  >
                    {Array.from({ length: item.maxPerOrder || 10 }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-4">
                  <p className="text-sm sm:text-base font-semibold tabular-nums text-foreground text-right">
                    {item.price === 0 ? t("cart.free") : formatBRL(item.price * item.quantity)}
                  </p>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="min-h-11 min-w-11 p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/5 rounded-xl transition-colors focus-visible:ring-2 focus-visible:ring-ring" aria-label={`${t("cart.removeAriaLabel")}: ${item.tierName}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-2xl">
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("cart.removeTicketTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t("cart.removeTicketDesc", { tierName: item.tierName }).replace("<strong>", "").replace("</strong>", "")}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">{t("cart.cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => removeItem(item.tierId)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-xl">
                          {t("cart.remove")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="w-full lg:w-[340px] shrink-0 lg:sticky lg:top-20">
          <div className="p-5 sm:p-6 rounded-2xl border border-border/70 bg-card space-y-5">
            <h2 className="font-display text-lg font-semibold tracking-tight text-foreground">{t("cart.summary")}</h2>
            <div className="space-y-3">
              <label htmlFor="cart-coupon" className="block text-sm font-medium text-foreground">{t("cart.couponCode")}</label>
              <div className="flex gap-2 w-full">
                <Input
                  id="cart-coupon"
                  autoCapitalize="characters"
                  spellCheck={false}
                  placeholder={t("cart.couponPlaceholder")}
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  className="h-11 min-w-0 rounded-xl text-base flex-1"
                />
                <Button
                  variant="outline"
                  onClick={handleValidateCoupon}
                  disabled={validatingCoupon}
                  className="h-11 shrink-0 rounded-xl px-4 whitespace-nowrap"
                >
                  {validatingCoupon ? t("cart.validating") : t("cart.apply")}
                </Button>
              </div>
            </div>
            <div className="space-y-3 text-sm border-t border-border/50 pt-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("cart.subtotal")}</span>
                <span className="font-medium text-foreground">{formatBRL(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("cart.serviceFee")}</span>
                <span className="font-medium text-foreground">{formatBRL(platformFee)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-success">
                  <span>{t("cart.discount")}</span>
                  <span className="font-medium">-{formatBRL(discount)}</span>
                </div>
              )}
              <div className="border-t border-border/50 pt-3 flex justify-between">
                <span className="font-semibold text-foreground">{t("cart.total")}</span>
                <span className="text-xl font-semibold tabular-nums text-foreground">{finalTotal === 0 ? t("cart.free") : formatBRL(finalTotal)}</span>
              </div>
            </div>
            <Button
              size="lg"
              className="hidden lg:flex w-full min-h-12 h-auto py-3 justify-center text-center whitespace-normal text-base font-semibold rounded-xl"
              onClick={handleCheckout}
              disabled={unavailableItems.length > 0}
            >
              {unavailableItems.length > 0 ? t("cart.itemsUnavailable") : t("cart.checkoutButton")}
            </Button>
            <AuthModal
              open={showAuth}
              onOpenChange={(open) => { setShowAuth(open); if (!open && user) navigate("/checkout"); }}
              redirectTo="/checkout"
            />
          </div>
        </div>
      </div>
      <div className={`fixed inset-x-0 ${user ? "bottom-[72px]" : "bottom-0"} z-40 border-t border-border bg-background px-4 pt-3 lg:hidden`} style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}>
        <div className="mx-auto flex w-full max-w-lg items-center justify-between gap-4">
          <p className="flex shrink-0 flex-col items-start text-left text-xs text-muted-foreground">
            <span>{t("cart.total")}</span>
            <span className="text-lg font-semibold tabular-nums text-foreground">{finalTotal === 0 ? t("cart.free") : formatBRL(finalTotal)}</span>
          </p>
          <Button className="flex min-w-0 flex-1 min-h-12 h-auto justify-center whitespace-normal rounded-xl px-3 py-3 text-center text-sm font-semibold" onClick={handleCheckout} disabled={unavailableItems.length > 0}>
            {unavailableItems.length > 0 ? t("cart.itemsUnavailable") : t("cart.checkoutButton")}
          </Button>
        </div>
      </div>
    </div>
  );
}
